import { Link, createSearchParams } from "react-router-dom";
import { useReactTable, createColumnHelper, getCoreRowModel } from "@tanstack/react-table";
import Badge from "react-bootstrap/Badge";
import { resources } from "@constants/hardcoded-strings";
import { DASHBOARD_URL, REPORTS_TRANSACTIONS_DETAILS_URL } from "@constants/routes";
import { PolicyDecisionTypes } from "@enums/policy-decision-types";
import { VerificationTransaction } from "@idw-types/data/reports";
import { injectTransactionId } from "@utils/parameter-injector";
import { IdwTableContainer } from "../../common/idw-table-container/idw-table-container";
import { IdwTable } from "../../common/idw-table/idw-table";
import styles from "./dashboard-verification-transactions-table-container.less";
import { useMemo } from "react";

type DashboardVerificationTransactionsTableContainerProps = {
    canViewTransactionReport: boolean;
    verificationTransactions: Array<VerificationTransaction>;
};

export const DashboardVerificationTransactionsTableContainer = (props: DashboardVerificationTransactionsTableContainerProps) => {
    const { canViewTransactionReport, verificationTransactions } = props;

    const generateTransactionURL = (transactionId: string) => {
        const url = injectTransactionId(REPORTS_TRANSACTIONS_DETAILS_URL, transactionId);
        const queryParametersObject: any = { previousPath: DASHBOARD_URL };
        const queryParameters = createSearchParams(queryParametersObject).toString();
        return `${url}?${queryParameters}`;
    };

    const columnHelper = createColumnHelper<VerificationTransaction>();

    const columns = useMemo(() => ([
        columnHelper.accessor("transactionID", {
            header: resources.tables.dashboardVerificationTransactions.columns.transactionId.header,
            cell: (props) => canViewTransactionReport ? (
                <Link className={styles.link} to={generateTransactionURL(props.row.original.transactionID)}>
                    {props.getValue()}
                </Link>
            ) : (<span>{props.getValue()}</span>)
        }),
        columnHelper.accessor("apikey", {
            header: resources.tables.dashboardVerificationTransactions.columns.apiKey.header
        }),
        columnHelper.accessor("credential", {
            header: resources.tables.dashboardVerificationTransactions.columns.credential.header
        }),
        columnHelper.accessor("score", {
            header: resources.tables.dashboardVerificationTransactions.columns.score.header
        }),
        columnHelper.accessor("policyDecision", {
            header: resources.tables.dashboardVerificationTransactions.columns.policyDecision.header,
            cell: (props) => {
                const value = props.getValue();
                switch (value) {
                    case PolicyDecisionTypes.APPROVED:
                        return <Badge bg={"success"}>{value}</Badge>;
                    case PolicyDecisionTypes.DENIED:
                        return <Badge bg={"danger"}>{value}</Badge>;
                    case PolicyDecisionTypes.NO_DECISION:
                        return <Badge bg={"secondary"}>{value}</Badge>;
                    case PolicyDecisionTypes.OBLIGATION:
                        return <Badge bg={"warning"}>{value}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }
            }
        })
    ]), [verificationTransactions]);

    const useTableResult: any = useReactTable({
        columns,
        data: verificationTransactions,
        getCoreRowModel: getCoreRowModel(),
        filterFns: null,
        sortDescFirst: false,
        enableSorting: false
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="dashboard-verification-transactions"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"dashboard-verification-transactions"}
            />
        </IdwTableContainer>
    );
};